'use client';

/**
 * Collapsible left rail — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/SidebarCollapsibleHeader.tsx}.
 * Registered as {@code header.style: 'sidebar-collapsible'}.
 *
 * <p>Differs from the server-rendered {@code SidebarHeader} in three ways:
 * <ol>
 *   <li>Click-to-toggle collapse — 240px expanded ↔ 72px collapsed.
 *       State persists in {@code localStorage}; survives navigation.
 *       220ms transition.</li>
 *   <li>Icon-first menu items. Collapsed mode shows only icons.</li>
 *   <li>Client component (the collapse state needs {@code useState}).
 *       Both styles coexist — tenants pick via
 *       {@code header.style}.</li>
 * </ol>
 *
 * <p>Props note: receives {@code tenantSlug} (a string) and builds
 * URLs internally. Function props can't cross the server→client
 * boundary in App Router.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import type { CSSProperties } from 'react';
import type { CurrentUser, MenuLink, ShellConfig } from '../../portal';
import { DEFAULT_I18N, isVisible, PortalLink, t } from '../../portal';
import { BlockIcon, guessSidebarIcon } from '../icons/sidebarIcons';
import { getMenuComponent } from '../menu/menuComponents';
import { shellHref } from '../shellHref';
import { HeaderActions } from './HeaderActions';

export interface SidebarCollapsibleHeaderProps {
  config: ShellConfig;
  tenantSlug: string;
  currentUser: CurrentUser | null;
  locale: string;
  /** Per-render override for the {@code categoriesMenu} menu component. */
  categoriesMenuOverride?: { feedSource: 'courses' | 'blog' | 'video' | 'micro' };
}

const COLLAPSE_KEY = 'lms.sidebar.collapsed';
const SIDEBAR_W_EXPANDED = 240;
const SIDEBAR_W_COLLAPSED = 72;
const HEADER_H = 80;
const AUTO_COLLAPSE_BREAKPOINT = 1250;

export function SidebarCollapsibleHeader({
  config,
  tenantSlug,
  currentUser,
  locale,
  categoriesMenuOverride,
}: SidebarCollapsibleHeaderProps) {
  // Aliased: `t` in this module is the platform LocalizedString resolver.
  const { t: uiText } = useTranslation('site-builder-runtime');
  const fallback = (config.i18n ?? DEFAULT_I18N).default;
  const rawBrand = config.brand ?? {};

  // logo fields may be stored as { light, dark } by ThemedImageInput — unwrap to strings
  function resolveImgSrc(
    v: string | { light?: string; dark?: string } | undefined,
    prefer: 'light' | 'dark' = 'light',
  ): string | undefined {
    if (!v) return undefined;
    if (typeof v === 'object') return prefer === 'dark' ? v.dark || v.light : v.light || v.dark;
    return v;
  }

  const brand = {
    ...rawBrand,
    logo: resolveImgSrc(
      rawBrand.logo as string | { light?: string; dark?: string } | undefined,
      'light',
    ),
    logoDark:
      resolveImgSrc(
        rawBrand.logoDark as string | { light?: string; dark?: string } | undefined,
        'dark',
      ) ||
      resolveImgSrc(
        rawBrand.logo as string | { light?: string; dark?: string } | undefined,
        'dark',
      ),
    logoIcon: resolveImgSrc(
      rawBrand.logoIcon as string | { light?: string; dark?: string } | undefined,
      'light',
    ),
    logoIconDark: resolveImgSrc(
      rawBrand.logoIconDark as string | { light?: string; dark?: string } | undefined,
      'dark',
    ),
  };
  const menu = (config.header?.menu ?? []).filter((m) =>
    isVisible(
      {
        requireAuth: m.requireAuth,
        requirePerms: m.requirePerms,
        requireDynamicGroupIds: m.requireDynamicGroupIds,
        requireSecurityGroupIds: m.requireSecurityGroupIds,
      },
      currentUser,
    ),
  );

  // Initialized to false on the server (avoids hydration mismatch
  // when localStorage isn't available); reads from localStorage on
  // mount and updates if needed.
  const [collapsed, setCollapsed] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  // Tracks whether the current collapsed state was forced by viewport
  // width so manual toggles can still override on wide screens.
  const autoCollapsedRef = useRef(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved === 'true') setCollapsed(true);
    } catch {
      /* localStorage may be unavailable */
    }
  }, []);

  // Auto-collapse on narrow viewports; restore on wide viewports.
  useEffect(() => {
    function handleResize() {
      const narrow = window.innerWidth <= AUTO_COLLAPSE_BREAKPOINT;
      setIsNarrow(narrow);
      if (narrow && !autoCollapsedRef.current) {
        autoCollapsedRef.current = true;
        setCollapsed(true);
      } else if (!narrow && autoCollapsedRef.current) {
        autoCollapsedRef.current = false;
        try {
          const saved = localStorage.getItem(COLLAPSE_KEY);
          setCollapsed(saved === 'true');
        } catch {
          setCollapsed(false);
        }
      }
    }
    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev;
      // Don't persist to localStorage when auto-collapsed by viewport —
      // the preference should reflect what the user chose on a wide screen.
      if (!autoCollapsedRef.current) {
        try {
          localStorage.setItem(COLLAPSE_KEY, String(next));
        } catch {
          /* swallow */
        }
      }
      return next;
    });
  }

  function tenantHref(href?: string): string {
    return shellHref(href, tenantSlug);
  }

  const width = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
  const brandName = t(brand.name, locale, fallback);
  const logoHeight = config.brand?.logoHeight ?? 52;
  // On narrow viewports, expanded sidebar overlays content instead of pushing it.
  const overlayMode = isNarrow && !collapsed;

  return (
    <>
      <style>{`
        .lms-sidebar-scroll {
          scrollbar-width: thin;
          scrollbar-color: var(--color-border-strong, rgba(255,255,255,0.25)) transparent;
        }
        .lms-sidebar-scroll::-webkit-scrollbar { width: 6px; }
        .lms-sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .lms-sidebar-scroll::-webkit-scrollbar-thumb {
          background: var(--color-border-strong, rgba(255,255,255,0.25));
          border-radius: 8px;
        }
        .lms-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: var(--color-text-secondary, rgba(255,255,255,0.4));
        }
      `}</style>
      {overlayMode && (
        <div
          onClick={() => setCollapsed(true)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 39,
            background: 'rgba(0,0,0,0.4)',
          }}
        />
      )}
      <aside
        className="lms-sidebar"
        style={{
          width: overlayMode ? SIDEBAR_W_EXPANDED : width,
          flexShrink: 0,
          background: 'var(--color-surface-card, #18181b)',
          position: overlayMode ? 'fixed' : 'sticky',
          top: 0,
          left: 0,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          borderRight: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
          zIndex: 40,
        }}
      >
        {/* Brand row + collapse toggle — always full width, never clipped. */}
        <div
          style={{
            width: SIDEBAR_W_EXPANDED,
            height: HEADER_H,
            boxSizing: 'border-box',
            background: 'inherit',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 15px',
            borderBottom: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
            gap: '12px',
          }}
        >
          {/* Collapsed + logoIcon set: the icon IS the expand button.
           *  YouTube/Slack pattern — one affordance per state. */}
          {collapsed && (brand.logoIcon || brand.logoIconDark) ? (
            <button
              type="button"
              onClick={toggle}
              aria-label={uiText('sidebar.expand')}
              style={{
                width: 40,
                height: 40,
                padding: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 8,
              }}
              title={
                brandName
                  ? uiText('sidebar.expandBrand', { brand: brandName })
                  : uiText('sidebar.expand')
              }
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  'var(--color-neutral-100, rgba(255,255,255,0.06))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              {brand.logoIcon && brand.logoIconDark ? (
                <>
                  <img
                    className="lms-logo--light"
                    src={brand.logoIcon}
                    alt={brandName}
                    style={{ width: 44, height: 44, objectFit: 'contain' }}
                  />
                  <img
                    className="lms-logo--dark"
                    src={brand.logoIconDark}
                    alt={brandName}
                    style={{ width: 44, height: 44, objectFit: 'contain' }}
                  />
                </>
              ) : (
                <img
                  src={brand.logoIcon || brand.logoIconDark}
                  alt={brandName}
                  style={{ width: 44, height: 44, objectFit: 'contain' }}
                />
              )}
            </button>
          ) : null}

          {/* Generic toggle button — renders unless we used logoIcon as
           *  the collapsed-mode trigger above. */}
          {!(collapsed && (brand.logoIcon || brand.logoIconDark)) && (
            <button
              type="button"
              onClick={toggle}
              aria-label={collapsed ? uiText('sidebar.expand') : uiText('sidebar.collapse')}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                border: 'none',
                background: 'transparent',
                color: 'var(--color-text-secondary, #a1a1aa)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 120ms ease, color 120ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  'var(--color-neutral-100, rgba(255,255,255,0.06))';
                e.currentTarget.style.color = 'var(--color-text-primary, #fafafa)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-secondary, #a1a1aa)';
              }}
            >
              <BlockIcon
                value={
                  collapsed
                    ? config.header?.expandIcon || config.header?.collapseIcon || 'menu'
                    : config.header?.collapseIcon || 'menu'
                }
                size={collapsed && (brand.logoIcon || brand.logoIconDark) ? 16 : 22}
              />
            </button>
          )}

          {/* Logo — always visible. Wraps a Link so clicking navigates
           *  to the brand home (logoHref, falling back to '/').
           *  In collapsed mode the brand row overflows the aside (which
           *  has no overflow:hidden so the 240px row spans into the
           *  content column). Disabling pointer-events prevents the
           *  invisible overflow region from intercepting sidebar-toggle
           *  and SidebarTopBar clicks. */}
          <PortalLink
            href={tenantHref((rawBrand.logoHref as string | undefined) ?? '/')}
            // Tenants commonly leave `brand.name` empty when the logo image
            // already spells out the wordmark (as BTK's does) — every <img>
            // below then gets `alt={brandName}` (empty), which is correct
            // per-image (the wordmark isn't decorative info duplicated
            // elsewhere) but leaves this link with NO accessible name at
            // all once every img alt is empty. An explicit aria-label here
            // guarantees one regardless of which logo variant renders.
            aria-label={brandName || (locale === 'tr' ? 'Ana Sayfa' : 'Home')}
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              flex: 1,
              minWidth: 0,
              paddingLeft: 8,
              textDecoration: 'none',
              // pointerEvents: collapsed ? 'none' : 'auto',
            }}
          >
            {brand.logo || brand.logoDark ? (
              <>
                {brand.logo && brand.logoDark ? (
                  // Fixed-size box (both dimensions) reserved before the image
                  // loads — see the identical comment in footers/index.tsx.
                  // A tenant logo's real aspect ratio is unknown ahead of
                  // time, so pinning only `height` (leaving width to the
                  // natural image size) still shifts the row once it loads.
                  <>
                    <div className="lms-logo--light" style={{ width: 160, height: logoHeight }}>
                      <img
                        src={brand.logo}
                        alt={brandName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'left center',
                        }}
                      />
                    </div>
                    <div className="lms-logo--dark" style={{ width: 160, height: logoHeight }}>
                      <img
                        src={brand.logoDark}
                        alt={brandName}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          objectPosition: 'left center',
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div style={{ width: 160, height: logoHeight }}>
                    <img
                      src={brand.logo || brand.logoDark}
                      alt={brandName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        objectPosition: 'left center',
                      }}
                    />
                  </div>
                )}
              </>
            ) : brand.logoIcon || brand.logoIconDark ? (
              <img
                src={brand.logoIcon || brand.logoIconDark}
                alt={brandName}
                style={{ width: 28, height: 28, objectFit: 'contain' }}
              />
            ) : brandName ? (
              <span
                style={{
                  fontSize: 'var(--font-size-base, 15px)',
                  fontWeight: 700,
                  color: 'var(--color-text-primary, #fafafa)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {brandName}
              </span>
            ) : (
              <BrandMark color="var(--color-brand-500, #8b5cf6)" />
            )}
          </PortalLink>
        </div>

        {/* Menu items. The <nav> is a PLAIN scroll container (block, not a
         *  flex column): `flex: 1` + `minHeight: 0` bound it to the leftover
         *  rail height, and `overflowY: auto` scrolls when the items don't
         *  fit. The items themselves live in an inner flex column with
         *  `flexShrink: 0`, so they keep their natural heights and overflow
         *  the scroll box instead of being compressed to fit — that
         *  compression (a flex column that is ALSO the scroll container) was
         *  why item heights changed and no scrollbar appeared. */}
        <nav
          className="lms-sidebar-scroll"
          style={{
            padding: 'var(--space-3, 12px) var(--space-2, 8px)',
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexShrink: 0 }}>
            {menu.map((item, i) => {
              // On narrow viewports the sidebar overlays content; close it
              // after navigation so the user sees the page they navigated to.
              const handleNavigate = isNarrow ? () => setCollapsed(true) : undefined;

              if (item.kind === 'group') {
                return (
                  <SidebarGroup
                    key={i}
                    label={t(item.label, locale, fallback)}
                    links={(item.links ?? []).filter((l) =>
                      isVisible(
                        {
                          requireAuth: l.requireAuth,
                          requirePerms: l.requirePerms,
                          requireDynamicGroupIds: l.requireDynamicGroupIds,
                          requireSecurityGroupIds: l.requireSecurityGroupIds,
                        },
                        currentUser,
                      ),
                    )}
                    tenantHref={tenantHref}
                    locale={locale}
                    fallback={fallback}
                    collapsed={collapsed}
                    onNavigate={handleNavigate}
                    showDivider={i !== 0}
                  />
                );
              }

              if (item.kind === 'component') {
                const Comp = getMenuComponent(item.componentId);
                if (!Comp) return null;
                const mergedProps: Record<string, unknown> = {
                  ...(item.props ?? {}),
                  ...(item.label !== undefined ? { label: item.label } : {}),
                  ...(item.allCategoriesLabel !== undefined
                    ? { allCategoriesLabel: item.allCategoriesLabel }
                    : {}),
                  ...(item.showCategoryIcons !== undefined
                    ? { showCategoryIcons: item.showCategoryIcons }
                    : {}),
                };
                if (item.componentId === 'categoriesMenu' && categoriesMenuOverride) {
                  mergedProps['feedSource'] = categoriesMenuOverride.feedSource;
                }
                // Cast placement to the union the registry expects; the
                // collapsed-sidebar variant isn't in the public placement
                // union yet — components that want to differentiate look
                // at the `collapsed` flag.
                return (
                  <Comp
                    key={i}
                    placement={'sidebar' as MenuComponentPlacement}
                    collapsed={collapsed}
                    tenantSlug={tenantSlug}
                    locale={locale}
                    fallbackLocale={fallback}
                    currentUser={currentUser}
                    props={mergedProps}
                  />
                );
              }

              // Plain link.
              if (!item.href) return null;
              const label = t(item.label, locale, fallback);
              const iconName = item.icon || guessSidebarIcon(label);
              const badge = t(item.badge, locale, fallback);
              return (
                <SidebarLink
                  key={i}
                  href={tenantHref(item.href)}
                  icon={iconName}
                  label={label}
                  badge={badge}
                  avatar={item.avatar}
                  unread={item.unread}
                  collapsed={collapsed}
                  onNavigate={handleNavigate}
                />
              );
            })}
          </div>
        </nav>

        {/* Right-cluster actions at the bottom — only when expanded.
          Stacked vertically since the rail is narrow. */}
        {!collapsed && config.header && (
          <div
            style={{
              marginTop: 'auto',
              padding: 16,
              borderTop: '1px solid var(--color-border-subtle, rgba(255,255,255,0.08))',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <HeaderActions
              header={config.header}
              locale={locale}
              i18nDefault={fallback}
              currentUser={currentUser}
              tenantHref={tenantHref}
            />
          </div>
        )}
      </aside>
    </>
  );
}

type MenuComponentPlacement = 'header-horizontal' | 'sidebar' | 'mobile-drawer';

// =============================================================================
// CollapsedDivider
// =============================================================================

/**
 * Horizontal rule shown between icon sections when the sidebar is
 * collapsed. Also rendered at the top and bottom of the icon list so
 * the rail reads as a clearly bounded set of sections. Uses the
 * mid-gray text token (not a near-invisible border token) so the line
 * stays visible in both light and dark themes.
 */
function CollapsedDivider() {
  return (
    <hr
      style={{
        margin: 'var(--space-4, 16px) var(--space-3, 12px)',
        border: 'none',
        borderTop: '1px solid var(--color-text-tertiary, #71717a)',
      }}
    />
  );
}

// =============================================================================
// SidebarGroup
// =============================================================================

function SidebarGroup({
  label,
  links,
  tenantHref,
  locale,
  fallback,
  collapsed,
  onNavigate,
  showDivider = true,
}: {
  label: string;
  links: MenuLink[];
  tenantHref: (href?: string) => string;
  locale: string;
  fallback: string;
  collapsed: boolean;
  onNavigate?: () => void;
  /** Leading divider — suppressed for the first group so no line sits at the rail's start. */
  showDivider?: boolean;
}) {
  return (
    <>
      {collapsed ? (
        showDivider ? (
          <CollapsedDivider />
        ) : null
      ) : (
        <div
          style={{
            padding: 'var(--space-3, 12px) var(--space-3, 12px) var(--space-1, 4px)',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: 'var(--font-tracking-widest, 0.08em)',
            // Not --color-text-tertiary: several shipped tenant themes leave
            // its $dark equal to $value (e.g. #71717a on both), which reads
            // fine on a light surface but drops to ~3.8:1 against a dark
            // sidebar — under the 4.5:1 AA minimum for this label's text
            // size. --color-text-secondary carries a real dark-mode override
            // in every theme checked and reads ~7:1 on dark surfaces.
            color: 'var(--color-text-secondary, #71717a)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {label}
        </div>
      )}
      {links.map((link, i) => {
        const linkLabel = t(link.label, locale, fallback);
        const iconName = link.icon || guessSidebarIcon(linkLabel);
        const badge = t(link.badge, locale, fallback);
        return (
          <SidebarLink
            key={i}
            href={tenantHref(link.href)}
            icon={iconName}
            label={linkLabel}
            badge={badge}
            avatar={link.avatar}
            unread={link.unread}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        );
      })}
    </>
  );
}

// =============================================================================
// SidebarLink
// =============================================================================

function SidebarLink({
  href,
  icon,
  label,
  badge,
  collapsed,
  avatar,
  unread,
  onNavigate,
}: {
  href: string;
  icon: string;
  label: string;
  badge?: string;
  collapsed: boolean;
  avatar?: string;
  unread?: boolean;
  onNavigate?: () => void;
}) {
  // Aliased: `t` in this module is the platform LocalizedString resolver.
  const { t: uiText } = useTranslation('site-builder-runtime');
  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: collapsed ? '10px 0' : '10px 16px',
    margin: collapsed ? '0 8px' : '0',
    borderRadius: 8,
    color: 'var(--color-text-primary, #fafafa)',
    textDecoration: 'none',
    fontSize: 'var(--font-size-sm, 14px)',
    fontWeight: 500,
    transition: 'background 120ms ease',
    justifyContent: collapsed ? 'center' : 'flex-start',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    position: 'relative',
  };

  return (
    <Link
      href={href}
      title={collapsed ? (badge ? `${label} — ${badge}` : label) : undefined}
      style={baseStyle}
      onClick={onNavigate}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--color-neutral-100, rgba(255,255,255,0.06))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 22,
          height: 22,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {avatar ? (
          <img
            src={avatar}
            alt=""
            style={{
              width: 20,
              height: 20,
              borderRadius: '50%',
              objectFit: 'cover',
              background: 'var(--color-surface-muted, #ececef)',
            }}
          />
        ) : (
          <BlockIcon value={icon} />
        )}
        {collapsed && badge && (
          <span
            style={{
              position: 'absolute',
              top: -2,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--color-brand-500, #8b5cf6)',
              border: '2px solid var(--color-surface-card, #18181b)',
              boxSizing: 'content-box',
            }}
            aria-hidden
          />
        )}
      </span>
      {!collapsed && (
        <>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '13px' }}>
            {label}
          </span>
          {unread && (
            <span
              aria-label={uiText('sidebar.unread')}
              style={{
                flexShrink: 0,
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: 'var(--color-brand-500, #4f46e5)',
              }}
            />
          )}
          {badge && (
            <span
              style={{
                flexShrink: 0,
                background: 'var(--color-brand-500, #8b5cf6)',
                color: 'var(--color-text-inverse, #fff)',
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: 'var(--font-tracking-wider, 0.05em)',
                padding: 'var(--space-0-5, 2px) var(--space-1-5, 6px)',
                borderRadius: 'var(--radius-sm, 4px)',
              }}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </Link>
  );
}

// =============================================================================
// BrandMark
// =============================================================================

function BrandMark({ color }: { color: string }) {
  // Stylized "play arrow inside hexagon" — fallback when the tenant
  // hasn't configured a logo. Scales well at any size.
  return (
    <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
      <path d="M16 2 L29 9.5 L29 22.5 L16 30 L3 22.5 L3 9.5 Z" fill={color} />
      <path d="M13 11 L13 21 L22 16 Z" fill="#fff" />
    </svg>
  );
}
